import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Box,
    TextField,
    Typography,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Paper,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { parseFormJSON } from "../utils/formParser";

const ImportDialog = ({ open, onClose, onImport }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [jsonText, setJsonText] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [parseResult, setParseResult] = useState(null);
    // The exact JSON string that was validated — passed verbatim to onImport so
    // raw / non-PDF smart forms can round-trip byte-for-byte without the lossy
    // parse->rebuild pipeline.
    const [validatedRaw, setValidatedRaw] = useState("");
    const [isValidating, setIsValidating] = useState(false);
    const fileInputRef = useRef(null);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        resetState();
    };

    const resetState = () => {
        setJsonText("");
        setSelectedFile(null);
        setParseResult(null);
        setValidatedRaw("");
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setParseResult(null);
            
            // Auto-validate on file selection
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target.result;
                validateJSON(content);
            };
            reader.readAsText(file);
        }
    };

    const handlePasteChange = (event) => {
        setJsonText(event.target.value);
        setParseResult(null);
    };

    const validateJSON = (jsonString) => {
        setIsValidating(true);
        
        // Small delay for UX (shows loading state)
        setTimeout(() => {
            const result = parseFormJSON(jsonString);
            setParseResult(result);
            setValidatedRaw(jsonString);
            setIsValidating(false);
        }, 300);
    };

    const handleValidate = () => {
        const jsonToValidate = activeTab === 0 ? jsonText : null;
        if (jsonToValidate) {
            validateJSON(jsonToValidate);
        }
    };

    const handleImport = () => {
        if (parseResult && parseResult.success) {
            // Pass the verbatim source string as a second arg; App uses it for
            // raw / non-PDF smart forms and ignores it for ordinary PDF forms.
            onImport(parseResult.data, validatedRaw);
            handleClose();
        }
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const canImport = parseResult && parseResult.success;

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: "600px",
                    maxHeight: "90vh"
                }
            }}
        >
            <DialogTitle>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Import Form Template
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Upload a JSON file or paste form template JSON
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
                >
                    <Tab 
                        icon={<ContentPasteIcon />} 
                        label="Paste JSON" 
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<CloudUploadIcon />} 
                        label="Upload File" 
                        iconPosition="start"
                    />
                </Tabs>

                {/* Tab 0: Paste JSON */}
                {activeTab === 0 && (
                    <Box>
                        <TextField
                            fullWidth
                            multiline
                            rows={12}
                            value={jsonText}
                            onChange={handlePasteChange}
                            placeholder='Paste your form JSON here...'
                            variant="outlined"
                            sx={{
                                fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                                fontSize: "13px",
                                "& .MuiInputBase-root": {
                                    fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                                    fontSize: "13px",
                                },
                            }}
                        />
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={handleValidate}
                                disabled={!jsonText.trim() || isValidating}
                            >
                                {isValidating ? "Validating..." : "Validate JSON"}
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Tab 1: Upload File */}
                {activeTab === 1 && (
                    <Box>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 4,
                                textAlign: "center",
                                cursor: "pointer",
                                borderStyle: "dashed",
                                borderWidth: 2,
                                borderColor: selectedFile ? "primary.main" : "divider",
                                bgcolor: selectedFile ? "action.hover" : "transparent",
                                transition: "all 0.2s",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    bgcolor: "action.hover",
                                },
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <CloudUploadIcon 
                                sx={{ 
                                    fontSize: 64, 
                                    color: selectedFile ? "primary.main" : "text.secondary",
                                    mb: 2 
                                }} 
                            />
                            <Typography variant="h6" gutterBottom>
                                {selectedFile ? selectedFile.name : "Click to select JSON file"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedFile ? "File loaded and validated" : "or drag and drop here"}
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json,application/json"
                                style={{ display: "none" }}
                                onChange={handleFileSelect}
                            />
                        </Paper>
                    </Box>
                )}

                {/* Validation Results */}
                {isValidating && (
                    <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>Validating form structure...</Typography>
                    </Box>
                )}

                {parseResult && !isValidating && (
                    <Box sx={{ mt: 3 }}>
                        {/* Success */}
                        {parseResult.success && (
                            <Alert 
                                severity="success" 
                                icon={<CheckCircleIcon />}
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Form validated successfully!
                                </Typography>
                                <Typography variant="body2">
                                    {parseResult.data.sections.length} sections, {parseResult.data.fields.length} fields
                                </Typography>
                            </Alert>
                        )}

                        {/* Errors */}
                        {parseResult.errors && parseResult.errors.length > 0 && (
                            <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Validation Errors
                                </Typography>
                                <List dense>
                                    {parseResult.errors.map((error, idx) => (
                                        <ListItem key={idx} sx={{ py: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <ErrorIcon fontSize="small" color="error" />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={error}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Alert>
                        )}

                        {/* Warnings */}
                        {parseResult.warnings && parseResult.warnings.length > 0 && (
                            <Alert severity="warning" icon={<WarningIcon />}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Warnings
                                </Typography>
                                <List dense>
                                    {parseResult.warnings.map((warning, idx) => (
                                        <ListItem key={idx} sx={{ py: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <WarningIcon fontSize="small" color="warning" />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={warning}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                                    You can still import this form, but some data may be missing.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={!canImport}
                    startIcon={<CheckCircleIcon />}
                >
                    Import JSON
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportDialog;
